
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  basePath?: string;
  onPageChange?: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, basePath, onPageChange }) => {
  const getPageLink = (page: number) => {
    if (onPageChange || !basePath) return '#';
    if (page === 1) {
      return basePath;
    }
    return `${basePath}/strona/${page}`;
  };

  const handlePageClick = (e: React.MouseEvent<HTMLAnchorElement>, page: number) => {
    if (onPageChange && page >= 1 && page <= totalPages) {
      e.preventDefault();
      onPageChange(page);
    }
  };

  const pages = [];
  const range = 2; // How many pages to show around the current page

  // Always show first page
  if (currentPage > range + 2) {
    pages.push(1);
    pages.push('...');
  } else {
    for (let i = 1; i < currentPage - range; i++) {
        pages.push(i);
    }
  }

  // Pages around current
  for (let i = Math.max(1, currentPage - range); i <= Math.min(totalPages, currentPage + range); i++) {
    pages.push(i);
  }
  
  // Always show last page
  if (currentPage < totalPages - range - 1) {
    pages.push('...');
    pages.push(totalPages);
  } else {
    for (let i = currentPage + range + 1; i <= totalPages; i++) {
        pages.push(i);
    }
  }
  
  const renderPageButton = (page: number, index: number) => (
    <a
      key={index}
      href={getPageLink(page)}
      onClick={(e) => handlePageClick(e, page)}
      className={`flex items-center justify-center w-10 h-10 rounded-full border transition-colors font-bold ${
        currentPage === page
          ? 'bg-mystic-accent text-black border-mystic-accent'
          : 'border-gray-700 text-gray-300 hover:bg-mystic-box'
      }`}
    >
      {page}
    </a>
  );


  return (
    <nav className="flex items-center justify-center gap-2">
      {/* Previous Button */}
      <a
        href={getPageLink(currentPage - 1)}
        onClick={(e) => handlePageClick(e, currentPage - 1)}
        rel={currentPage > 1 ? "prev" : undefined}
        aria-label="Poprzednia strona"
        className={`flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${
          currentPage === 1
            ? 'border-gray-800 text-gray-600 pointer-events-none'
            : 'border-gray-700 text-gray-300 hover:bg-mystic-box'
        }`}
      >
        <ChevronLeft className="w-4 h-4" />
      </a>

      {/* Page Numbers */}
      {pages.map((page, index) =>
        typeof page === 'number' ? (
          renderPageButton(page, index)
        ) : (
          <span key={index} className="flex items-center justify-center w-10 h-10 text-gray-500">
            ...
          </span>
        )
      )}

      {/* Next Button */}
      <a
        href={getPageLink(currentPage + 1)}
        onClick={(e) => handlePageClick(e, currentPage + 1)}
        rel={currentPage < totalPages ? "next" : undefined}
        aria-label="Następna strona"
        className={`flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${
          currentPage === totalPages
            ? 'border-gray-800 text-gray-600 pointer-events-none'
            : 'border-gray-700 text-gray-300 hover:bg-mystic-box'
        }`}
      >
        <ChevronRight className="w-4 h-4" />
      </a>
    </nav>
  );
};

export default Pagination;
